import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { BookOpen, ArrowLeft, Key, Check, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [step, setStep] = useState(1); // 1: request, 2: confirm
  const [username, setUsername] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generatedToken, setGeneratedToken] = useState("");

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/password-reset/request`, { username });
      if (response.data.reset_token) {
        setGeneratedToken(response.data.reset_token);
        setStep(2);
        toast.success("Reset-kode generert!", {
          description: "Kopier koden og bruk den til å tilbakestille passordet."
        });
      }
    } catch (error) {
      toast.error("Noe gikk galt", {
        description: "Vennligst prøv igjen."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passordene stemmer ikke overens");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Passord må være minst 6 tegn");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token: resetToken || generatedToken,
        new_password: newPassword
      });
      toast.success("Passord oppdatert!", {
        description: "Du kan nå logge inn med ditt nye passord."
      });
      setStep(3);
    } catch (error) {
      toast.error("Tilbakestilling feilet", {
        description: error.response?.data?.detail || "Ugyldig eller utløpt kode."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" />
            Tilbake til innlogging
          </Link>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
              <Key className="w-8 h-8 text-primary-foreground" />
            </div>
          </div>
          <h1 className="font-display text-2xl font-semibold text-foreground tracking-tight">
            Tilbakestill passord
          </h1>
        </div>

        {step === 1 && (
          <Card className="bg-card border-border/50 shadow-lg">
            <CardHeader className="text-center pb-4">
              <CardTitle className="font-display text-xl">Steg 1: Be om reset</CardTitle>
              <CardDescription>
                Skriv inn brukernavnet ditt for å få en reset-kode
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRequestReset} className="space-y-6" data-testid="reset-request-form">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-foreground">Brukernavn</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Ditt brukernavn"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="bg-background/50 border-border focus:ring-primary"
                    data-testid="reset-username-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full hover:scale-105 transition-transform"
                  disabled={isLoading}
                  data-testid="request-reset-button"
                >
                  {isLoading ? "Sender..." : "Få reset-kode"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="bg-card border-border/50 shadow-lg">
            <CardHeader className="text-center pb-4">
              <CardTitle className="font-display text-xl">Steg 2: Nytt passord</CardTitle>
              <CardDescription>
                Bruk koden til å sette et nytt passord
              </CardDescription>
            </CardHeader>
            <CardContent>
              {generatedToken && (
                <div className="mb-6 p-4 bg-muted/50 rounded-xl">
                  <p className="text-sm text-muted-foreground mb-2">Din reset-kode:</p>
                  <code className="block p-3 bg-background rounded-lg text-xs break-all font-mono text-foreground" data-testid="generated-token">
                    {generatedToken}
                  </code>
                </div>
              )}
              <form onSubmit={handleConfirmReset} className="space-y-6" data-testid="reset-confirm-form">
                <div className="space-y-2">
                  <Label htmlFor="token" className="text-foreground">Reset-kode</Label>
                  <Input
                    id="token"
                    type="text"
                    placeholder="Lim inn koden her"
                    value={resetToken || generatedToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    required
                    className="bg-background/50 border-border focus:ring-primary font-mono text-sm"
                    data-testid="reset-token-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-foreground">Nytt passord</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="bg-background/50 border-border focus:ring-primary"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-foreground">Bekreft passord</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-background/50 border-border focus:ring-primary"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full hover:scale-105 transition-transform"
                  disabled={isLoading}
                  data-testid="confirm-reset-button"
                >
                  {isLoading ? "Oppdaterer..." : "Oppdater passord"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="bg-card border-border/50 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-display text-xl font-semibold text-foreground mb-2">
                Passord oppdatert!
              </h2>
              <p className="text-muted-foreground mb-6">
                Ditt passord er nå endret. Du kan logge inn med det nye passordet.
              </p>
              <Link to="/admin">
                <Button className="rounded-full" data-testid="go-to-login-button">
                  Gå til innlogging
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
